import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import toast from "react-hot-toast";
import useRecruiterJobs from "../hooks/useRecruiterJobs";
import {
  getRecruiterInterviewReport,
  getJobResultsForRecruiter,
  getCandidateAssessmentReport,
  downloadCandidateReportHtml,
  downloadCsv,
  JOB_MODE_LABELS,
  EVENT_TYPE_LABELS,
  listRecruiterInterviews,
  allowMcqCodingRetake,
  allowInterviewRetake,
  regradeCodingSubmission,
} from "../services/jobService";
import CreateJobModal from "./CreateJobModal";
import CreateJobWizard from "./CreateJobWizard";
import ResumeUploadModal from "./ResumeUploadModal";
import JobBrandingModal from "./JobBrandingModal";
import PipelineCandidateTable from "./PipelineCandidateTable";
import QuestionBankManager from "./QuestionBankManager";
import { DashboardFilters, DashboardSkeleton, formatDate } from "./RecruiterDashboardSections";
import RecruiterReportDetail from "./RecruiterReportDetail";
import "./RecruiterJobs.css";
import "./QuestionBankManager.css";

function EventTypeBadge({ eventType, includeCoding, pipeline }) {
  // Pipeline jobs are stored with eventType INTERVIEW (the job itself hosts
  // the AI Interview round), so they need their own badge to be told apart.
  if (pipeline) return <span className="job-event-badge pipeline">Hiring Pipeline</span>;
  const type = eventType || "INTERVIEW";
  const label = type === "MCQ" && includeCoding ? "MCQ + Coding" : (EVENT_TYPE_LABELS[type] || type);
  return <span className={`job-event-badge ${type.toLowerCase()}`}>{label}</span>;
}

/* ── helpers ──────────────────────────────────────────────── */
const PIPELINE_SHORT_LABELS = {
  resume: "Resume", form: "Form", mcq: "MCQ", coding: "Coding", both: "MCQ + Coding", ai: "AI Interview", rank: "Rank"
};

function pipelineSummary(stages) {
  return (stages || []).map((s) => PIPELINE_SHORT_LABELS[s] || s).join(" → ");
}

function getInterviewLink(jobId) {
  return `${window.location.origin}/interview/${jobId}`;
}

function computeOverall(interview) {
  const { technicalScore: t, communicationScore: c, confidenceScore: conf } = interview;
  const vals = [t, c, conf].filter((v) => v != null);
  if (!vals.length) return null;
  return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
}

function scoreColor(v) {
  if (v == null) return "pending";
  if (v >= 80) return "good";
  if (v >= 60) return "warn";
  return "bad";
}

// Formats in the recruiter's own local timezone (unlike a raw UTC string),
// so "when was this test taken" reads correctly regardless of where the
// recruiter is viewing the dashboard from.
function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const PAGE_SIZE = 30;
function paginateRows(rows, page) {
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  return { rows: rows.slice(start, start + PAGE_SIZE), currentPage, totalPages, start, end: Math.min(start + PAGE_SIZE, rows.length) };
}

/* ── small components ─────────────────────────────────────── */
function ScoreBar({ value }) {
  if (value == null) return <span className="rd-score-empty">N/A</span>;
  const cls = scoreColor(value);
  return (
    <div className="rd-score-bar">
      <div className="rd-score-track"><span className={`rd-score-fill ${cls}`} style={{ width: `${value}%` }} /></div>
      <span className={`rd-score-val ${cls}`}>{value}</span>
    </div>
  );
}

function RankBadge({ rank }) {
  const cls = rank === 1 ? "gold" : rank === 2 ? "silver" : rank === 3 ? "bronze" : "";
  return <span className={`rd-rank ${cls}`}>#{rank}</span>;
}

function IntegrityRing({ score, level, flags, onOpenReport }) {
  const r = 13, circ = 2 * Math.PI * r;
  const dash = ((score ?? 0) / 100) * circ;
  const col = level === "high" ? "#36d399" : level === "medium" ? "#f59e0b" : "#f87171";
  return (
    <div className="rd-integrity">
      <div className="rd-integrity-ring">
        <svg viewBox="0 0 32 32" width="34" height="34">
          <circle cx="16" cy="16" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
          <circle cx="16" cy="16" r={r} fill="none" stroke={col} strokeWidth="3"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            transform="rotate(-90 16 16)" />
        </svg>
        <div className="rd-integrity-pct">{score ?? "—"}</div>
      </div>
      <div>
        <div className={`rd-integrity-lvl lvl-${level ?? "pending"}`}>
          {level ?? "pending"}
        </div>
        {flags?.length > 0
          ? <details className="rd-flags"><summary>{flags.length} flag{flags.length > 1 ? "s" : ""}</summary><ul>{flags.map((f, i) => <li key={i}>{f}</li>)}</ul></details>
          : <span className="rd-flags-clean">Clean</span>}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toUpperCase();
  return <span className={`rd-pill ${s === "ACTIVE" ? "active" : "inactive"}`}>{s === "ACTIVE" ? "Active" : "Inactive"}</span>;
}

function PipelineBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="rd-pipe-row">
      <span className="rd-pipe-label"><span className="rd-pipe-dot" style={{ background: color }} />{label}</span>
      <span className="rd-pipe-num">{value}</span>
      <div className="rd-pipe-bar"><span style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
}

function KpiCard({ label, value, detail, accent }) {
  return (
    <div className={`summary-card${accent ? " summary-card--accent" : ""}`}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function ScatterTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <div className="rd-tooltip">
      <div className="rd-tooltip-name">{d.name}</div>
      <div>Technical: <strong>{d.technical}</strong></div>
      <div>Overall: <strong>{d.overall}</strong></div>
      <div>Rank: <strong>#{d.rank}</strong></div>
    </div>
  );
}

function AssessmentScatterTip({ active, payload, xLabel }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <div className="rd-tooltip">
      <div className="rd-tooltip-name">{d.name}</div>
      <div>{xLabel}: <strong>{d.x}</strong></div>
      <div>Integrity: <strong>{d.y}</strong></div>
      <div>Rank: <strong>#{d.rank}</strong></div>
    </div>
  );
}

function PaginationControls({ label, total, page, totalPages, start, end, onPageChange }) {
  if (total <= PAGE_SIZE) return <div className="table-pagination"><span>{total ? `${total} ${label}` : `No ${label}`}</span></div>;
  return (
    <div className="table-pagination">
      <span>Showing {start + 1}–{end} of {total} {label}</span>
      <div className="pagination-actions">
        <button className="secondary-button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Previous</button>
        <strong>Page {page} of {totalPages}</strong>
        <button className="secondary-button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</button>
      </div>
    </div>
  );
}

/* ── MCQ / Coding results (same page shell as the AI-interview view) ──── */
function AssessmentResultsSection({ jobId, eventType, loading, error, data, onRetakeGranted }) {
  // Keyed by the row's own submission id (not uid) — a candidate can have
  // more than one row/submission for the same job after a retake, and each
  // row's own Report click should only disable itself, not every row for
  // that candidate.
  const [downloadingId, setDownloadingId] = useState(null);
  const [retakingUid, setRetakingUid] = useState(null);
  const [regradingId, setRegradingId] = useState(null);

  const handleDownloadReport = async (uid, submissionId) => {
    setDownloadingId(submissionId);
    try {
      const report = await getCandidateAssessmentReport(jobId, uid, submissionId);
      downloadCandidateReportHtml(report);
    } catch (err) {
      toast.error(err.message || "Unable to load candidate report");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleAllowRetake = async (uid) => {
    if (!window.confirm(
      "Allow this candidate to take the assessment again? Their attempt lock will be cleared " +
      "so they can start fresh — this does not delete their past results."
    )) return;
    setRetakingUid(uid);
    try {
      await allowMcqCodingRetake(jobId, uid);
      toast.success("Candidate can now retake this assessment.");
      onRetakeGranted?.();
    } catch (err) {
      toast.error(err.message || "Unable to allow retake");
    } finally {
      setRetakingUid(null);
    }
  };

  // submissionId is the CODING submission doc's own id — for a pure Coding
  // job that's the row's own id; for a combined MCQ+Coding job the coding
  // half is a separate submission doc (coding_submission_id).
  const handleRegrade = async (submissionId) => {
    setRegradingId(submissionId);
    try {
      await regradeCodingSubmission(jobId, submissionId);
      toast.success("Grading queued. Results will update when ready.");
      onRetakeGranted?.(); // same underlying reload; name is retake-specific, action isn't
    } catch (err) {
      toast.error(err.message || "Unable to regrade this submission");
    } finally {
      setRegradingId(null);
    }
  };

  if (loading) {
    return <div className="recruiter-empty">Loading results…</div>;
  }
  if (error) {
    return <div className="recruiter-alert"><span>{error}</span></div>;
  }
  if (!data || !data.results || data.results.length === 0) {
    return <div className="recruiter-empty">No candidates have completed this assessment yet.</div>;
  }

  const isMcq = eventType === "MCQ";
  const results = data.results;
  const total = results.length;
  const hasCoding = results.some((r) => r.coding_total != null);
  // Whether ANY coding grading exists for this job at all — a pure Coding
  // job always does; an MCQ job only does when the recruiter enabled the
  // combined MCQ+Coding round.
  const hasCodingComponent = !isMcq || hasCoding;
  const avgScore = isMcq
    ? Math.round(results.reduce((s, r) => s + (r.scores?.total || 0), 0) / total)
    : null;
  const highIntegrityPct = Math.round(results.filter((r) => r.integrity_level === "high").length / total * 100);

  const xLabel = isMcq ? "Score %" : "Solved";
  const xMax = isMcq ? 100 : Math.max(...results.map((r) => r.total || 0), 1);
  const scatterData = [...results]
    .sort((a, b) => (isMcq ? (b.scores?.total || 0) - (a.scores?.total || 0) : (b.solved || 0) - (a.solved || 0)))
    .map((r, i) => ({
      name: r.name || r.email || "Candidate",
      x: isMcq ? (r.scores?.total || 0) : (r.solved || 0),
      y: r.integrity_score ?? 100,
      rank: i + 1
    }));

  return (
    <>
      <div className="summary-card-grid">
        <KpiCard label="Candidates" value={total} detail="completed" />
        {isMcq
          ? <KpiCard label="Avg. Score" value={`${avgScore}%`} detail="out of 100" />
          : <KpiCard label="Avg. Solved" value={Math.round(results.reduce((s, r) => s + (r.solved || 0), 0) / total * 10) / 10} detail={`out of ${results[0]?.total ?? "—"} problems`} />}
        <KpiCard label="High Integrity" value={`${highIntegrityPct}%`} detail={`${results.filter(r => r.integrity_level !== "high").length} flagged`} accent />
      </div>

      <div className="recruiter-section" style={{ marginTop: "1rem" }}>
        <div className="section-heading">
          <h2>Score distribution</h2>
          <span className="section-pill">{xLabel} vs. Integrity</span>
        </div>
        <div className="rd-scatter-wrap">
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 12, right: 16, bottom: 24, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
              <XAxis dataKey="x" type="number" domain={[0, xMax]}
                tick={{ fill: "rgba(255,255,255,0.42)", fontSize: 10 }}
                label={{ value: `${xLabel} →`, position: "insideBottomRight", offset: -4, fill: "rgba(255,255,255,0.38)", fontSize: 10 }} />
              <YAxis dataKey="y" type="number" domain={[0, 100]}
                tick={{ fill: "rgba(255,255,255,0.42)", fontSize: 10 }}
                label={{ value: "Integrity →", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.38)", fontSize: 10 }} />
              <Tooltip content={<AssessmentScatterTip xLabel={xLabel} />} cursor={{ stroke: "rgba(255,255,255,0.12)" }} />
              <Scatter data={scatterData}
                shape={(props) => {
                  const { cx, cy, payload } = props;
                  const top = payload.x >= xMax * 0.75 && payload.y >= 75;
                  const r = payload.rank <= 3 ? 7 : 5;
                  return (
                    <g>
                      <circle cx={cx} cy={cy} r={r + 3} fill={top ? "#f59e0b" : "#764ba2"} fillOpacity={0.15} />
                      <circle cx={cx} cy={cy} r={r} fill={top ? "#f59e0b" : "#667eea"} stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
                    </g>
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="recruiter-section" style={{ marginTop: "1rem" }}>
        <div className="section-heading">
          <h2>Candidate results</h2>
          <span className="section-pill">{total} total</span>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table candidate-detail-table">
            <thead>
              <tr>
                <th>Candidate</th>
                {isMcq ? <><th>Score</th><th>Correct</th><th>Time used</th></> : <th>Problems solved</th>}
                {hasCoding && <th>Coding</th>}
                <th>Integrity</th>
                <th>Date &amp; Time</th>
                <th style={{ width: 90 }}>Report</th>
                <th style={{ width: 100 }}>Retake</th>
                {hasCodingComponent && <th style={{ width: 110 }}>Grading</th>}
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="rd-cand-name">
                      <div className="rd-cand-avatar">{(r.name || r.email || "??").slice(0, 2).toUpperCase()}</div>
                      <div>
                        <div className="rd-cand-display">{r.name || "Candidate"}</div>
                        <div className="rd-cand-email">{r.email || ""}</div>
                      </div>
                    </div>
                  </td>
                  {isMcq ? (
                    <>
                      <td><span className={`rd-overall ${scoreColor(r.scores?.total)}`}>{r.scores?.total ?? "—"}</span></td>
                      <td>{r.correct_answers ?? 0}/{r.total_questions ?? 0}</td>
                      <td>{r.time_used_ms ? `${Math.round(r.time_used_ms / 60000)} min` : "—"}</td>
                    </>
                  ) : (
                    <td title={(r.submissions || []).map((s) => `${s.title || "Problem"}: ${s.status || "not_run"}`).join("\n")}>
                      {r.grading_status && r.grading_status !== 'completed'
                        ? (['needs_reconciliation', 'dead_letter'].includes(r.grading_status) ? 'Needs attention' : 'Grading…')
                        : `${r.solved ?? 0}/${r.total ?? 0}`}
                    </td>
                  )}
                  {hasCoding && (
                    <td title={(r.coding_submissions || []).map((s) => `${s.title || "Problem"}: ${s.status || "not_run"}`).join("\n")}>
                      {r.grading_status && r.grading_status !== 'completed'
                        ? (['needs_reconciliation', 'dead_letter'].includes(r.grading_status) ? 'Needs attention' : 'Grading…')
                        : r.coding_total != null ? `${r.coding_solved ?? 0}/${r.coding_total}` : "—"}
                    </td>
                  )}
                  <td>
                    <IntegrityRing
                      score={r.integrity_score}
                      level={r.integrity_level}
                      flags={r.screen_share_unsupported ? ["Screen share unavailable on candidate's device (iOS)"] : []}
                    />
                  </td>
                  <td className="rd-muted">{formatDateTime(r.created_at_iso)}</td>
                  <td>
                    <button
                      className="rd-action-btn"
                      type="button"
                      disabled={downloadingId === r.id}
                      onClick={() => handleDownloadReport(r.uid, r.id)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                      {downloadingId === r.id ? "…" : "Report"}
                    </button>
                  </td>
                  <td>
                    {r.retake_pending ? (
                      <span
                        className="rd-retake-pending"
                        title="This candidate's attempt lock was cleared and they haven't submitted a new attempt yet"
                      >
                        ✓ Retake allowed
                      </span>
                    ) : (
                      <button
                        className="rd-action-btn"
                        type="button"
                        title="Clear this candidate's attempt lock so they can take the assessment again"
                        disabled={retakingUid === r.uid}
                        onClick={() => handleAllowRetake(r.uid)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 1 3 6.7M3 21v-6h6"/></svg>
                        {retakingUid === r.uid ? "…" : "Allow Retake"}
                      </button>
                    )}
                  </td>
                  {hasCodingComponent && (() => {
                    const codingSubmissionId = isMcq ? r.coding_submission_id : r.id;
                    return (
                      <td>
                        {r.needs_regrade && codingSubmissionId ? (
                          <button
                            className="rd-action-btn rd-action-btn--warn"
                            type="button"
                            title="A code check couldn't reach the verifier the first time — re-run grading for this submission"
                            disabled={regradingId === codingSubmissionId}
                            onClick={() => handleRegrade(codingSubmissionId)}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 1 3 6.7M3 21v-6h6"/></svg>
                            {regradingId === codingSubmissionId ? "…" : "Regrade"}
                          </button>
                        ) : (
                          <span className="rd-muted">—</span>
                        )}
                      </td>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════ */
export default function RecruiterJobsDashboard({ user }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [pipelineWizardOpen, setPipelineWizardOpen] = useState(false);
  const [resumeUploadOpen, setResumeUploadOpen] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [candidateTableRefreshKey, setCandidateTableRefreshKey] = useState(0);
  const [pipelineCandidateCount, setPipelineCandidateCount] = useState(0);
  const [manageQuestionsJob, setManageQuestionsJob] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [modeFilter, setModeFilter] = useState("ALL");
  const [interviews, setInterviews] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [recordingPreview, setRecordingPreview] = useState(null);
  const [retakingInterviewId, setRetakingInterviewId] = useState(null);
  const [rolePage, setRolePage] = useState(1);
  const [candidatePage, setCandidatePage] = useState(1);
  const [interviewsLoading, setInterviewsLoading] = useState(true);
  const [interviewsError, setInterviewsError] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportPending, setReportPending] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [jobTabFilter, setJobTabFilter] = useState("ALL"); // ALL | ACTIVE | INACTIVE
  const [assessmentResults, setAssessmentResults] = useState(null); // MCQ/CODING results for selectedJob
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState("");

  const { jobs, loading, saving, error, reload, createJob, toggleJobStatus } = useRecruiterJobs(user);

  const loadInterviews = useCallback(async (silent = false) => {
    try {
      if (!silent) setInterviewsLoading(true);
      if (!silent) setInterviewsError("");
      setInterviews(await listRecruiterInterviews());
    } catch (err) {
      if (!silent) setInterviewsError(err.message || "Unable to load candidate interviews");
    } finally {
      if (!silent) setInterviewsLoading(false);
    }
  }, []);

  useEffect(() => { if (user?.uid) loadInterviews(); }, [loadInterviews, user?.uid]);

  // Self-updating dashboard: poll for new/updated candidates instead of requiring a manual
  // refresh. Silent (no loading skeleton) so it doesn't disrupt whatever the recruiter is
  // looking at. Paused while the tab is hidden, and refreshes immediately on becoming visible
  // again so switching back to the tab never shows stale data for up to a full interval.
  useEffect(() => {
    if (!user?.uid) return undefined;

    const POLL_INTERVAL_MS = 30000;
    let intervalId = null;

    const refresh = () => {
      reload(true);
      loadInterviews(true);
    };

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(refresh, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === "visible") startPolling();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user?.uid, reload, loadInterviews]);

  /* ── filtered jobs ── */
  const filteredJobs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return jobs.filter((job) => {
      if (term && ![job.jobId, job.title, job.companyName].some((v) => String(v || "").toLowerCase().includes(term))) return false;
      if (statusFilter !== "ALL" && job.status !== statusFilter) return false;
      if (modeFilter !== "ALL" && job.mode !== modeFilter) return false;
      return true;
    });
  }, [jobs, modeFilter, search, statusFilter]);

  const filteredJobIds = useMemo(() => new Set(filteredJobs.map((j) => j.jobId)), [filteredJobs]);
  const filteredInterviews = useMemo(() => interviews.filter((i) => filteredJobIds.has(i.jobId)), [filteredJobIds, interviews]);

  /* ── tab-filtered jobs for the table ── */
  const tabFilteredJobs = useMemo(() => {
    if (jobTabFilter === "ALL") return filteredJobs;
    return filteredJobs.filter((j) => j.status === jobTabFilter);
  }, [filteredJobs, jobTabFilter]);

  const selectedJob = useMemo(() => filteredJobs.find((j) => j.jobId === selectedJobId) || null, [filteredJobs, selectedJobId]);
  const selectedJobInterviews = useMemo(() => interviews.filter((i) => i.jobId === selectedJobId), [interviews, selectedJobId]);
  const isAssessmentJob = selectedJob && selectedJob.eventType && selectedJob.eventType !== "INTERVIEW";
  // Jobs created via the new Create Hiring Pipeline wizard carry pipelineStages —
  // legacy jobs never have this field, so their rendering below is unaffected.
  const isPipelineJob = !!(selectedJob && selectedJob.pipelineStages && selectedJob.pipelineStages.length > 0);

  const loadAssessmentResults = useCallback(async (silent = false) => {
    if (!silent) { setAssessmentLoading(true); setAssessmentError(""); }
    try {
      const data = await getJobResultsForRecruiter(selectedJobId);
      setAssessmentResults(data);
    } catch (err) {
      if (!silent) setAssessmentError(err.message || "Unable to load results");
    } finally {
      if (!silent) setAssessmentLoading(false);
    }
  }, [selectedJobId]);

  useEffect(() => {
    if (!isAssessmentJob) { setAssessmentResults(null); setAssessmentError(""); return; }
    loadAssessmentResults();
  }, [isAssessmentJob, selectedJobId]); // eslint-disable-line

  useEffect(() => {
    const pending = assessmentResults?.results?.some(row =>
      ['queued', 'running', 'retry'].includes(row.grading_status));
    if (!isAssessmentJob || !pending) return undefined;
    let timer;
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'hidden') timer = setTimeout(refreshWhenVisible, 15000);
      else loadAssessmentResults(true);
    };
    timer = setTimeout(refreshWhenVisible, 15000);
    return () => clearTimeout(timer);
  }, [isAssessmentJob, assessmentResults, loadAssessmentResults]);

  /* ── ranked candidates for selected job (adds .rank and ._overall) ── */
  const rankedCandidates = useMemo(() =>
    [...selectedJobInterviews]
      .map((i) => ({ ...i, _overall: computeOverall(i) }))
      .sort((a, b) => (b._overall ?? -1) - (a._overall ?? -1))
      .map((c, i) => ({ ...c, rank: i + 1 })),
    [selectedJobInterviews]
  );

  // A retake is "pending" (candidate unlocked but hasn't used it yet) when
  // THIS candidate's most recently started interview is the one that got
  // retakeAllowed set on it — once they actually retake, a newer interview
  // doc without that flag becomes the latest, and this flips back off.
  const retakePendingByUid = useMemo(() => {
    const latestByUid = {};
    for (const i of selectedJobInterviews) {
      if (!i.candidateUid) continue;
      const prev = latestByUid[i.candidateUid];
      if (!prev || new Date(i.startedAt || 0) > new Date(prev.startedAt || 0)) {
        latestByUid[i.candidateUid] = i;
      }
    }
    const pending = {};
    for (const [uid, latest] of Object.entries(latestByUid)) {
      pending[uid] = !!latest.retakeAllowed;
    }
    return pending;
  }, [selectedJobInterviews]);

  const paginatedJobs = useMemo(() => paginateRows(tabFilteredJobs, rolePage), [tabFilteredJobs, rolePage]);
  // Paginate rankedCandidates (not the raw selectedJobInterviews) -- the table below reads
  // .rank and ._overall, which only exist on the ranked/derived objects.
  const paginatedCandidates = useMemo(() => paginateRows(rankedCandidates, candidatePage), [candidatePage, rankedCandidates]);

  useEffect(() => { setRolePage(1); }, [modeFilter, search, statusFilter, jobTabFilter]);
  useEffect(() => { setCandidatePage(1); }, [selectedJobId]);
  useEffect(() => { if (selectedJobId && !selectedJob) setSelectedJobId(""); }, [selectedJob, selectedJobId]);

  /* ── derived dashboard metrics ── */
  const completedInterviews = useMemo(() => filteredInterviews.filter((i) => i.status === "COMPLETED"), [filteredInterviews]);
  const avgOverall = useMemo(() => {
    const vals = completedInterviews.map(computeOverall).filter((v) => v != null);
    return vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1) : "—";
  }, [completedInterviews]);
  const flaggedCount = useMemo(() => filteredInterviews.filter((i) => i.integrityScore != null && i.integrityScore < 70).length, [filteredInterviews]);

  /* ── pipeline counts ── */
  const pipelineInvited   = filteredInterviews.length;
  const pipelineStarted   = filteredInterviews.filter((i) => i.startedAt || i.status === "COMPLETED").length;
  const pipelineCompleted = completedInterviews.length;
  const pipelineMax       = Math.max(pipelineInvited, 1);

  /* ── recent activity (last 5) ── */
  const recentActivity = useMemo(() =>
    [...filteredInterviews]
      .sort((a, b) => new Date(b.completedAt || b.startedAt || 0) - new Date(a.completedAt || a.startedAt || 0))
      .slice(0, 5),
    [filteredInterviews]
  );

  /* ── per-job average score ── */
  function jobAvgScore(jobId) {
    const vals = interviews.filter((i) => i.jobId === jobId && i.status === "COMPLETED").map(computeOverall).filter((v) => v != null);
    return vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1) : null;
  }

  /* ── skill averages for selected job ── */
  const skillAverages = useMemo(() => {
    const skills = [
      { label: "Technical",     key: "technicalScore" },
      { label: "Communication", key: "communicationScore" },
      { label: "Confidence",    key: "confidenceScore" },
    ];
    return skills.map(({ label, key }) => {
      const vals = selectedJobInterviews.filter((i) => i[key] != null).map((i) => i[key]);
      return { label, avg: vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0 };
    });
  }, [selectedJobInterviews]);

  /* ── scatter data ── */
  const scatterData = useMemo(() =>
    rankedCandidates.map((c) => ({
      name: c.candidateName || `Cand-${(c.interviewId || "").slice(-4)}`,
      technical: c.technicalScore ?? 0,
      overall: c._overall ?? 0,
      rank: c.rank,
    })),
    [rankedCandidates]
  );

  /* ── job results mini-KPIs ── */
  const jobAvgTech = useMemo(() => {
    const vals = selectedJobInterviews.filter((i) => i.technicalScore != null).map((i) => i.technicalScore);
    return vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1) : "—";
  }, [selectedJobInterviews]);

  const jobAvgOverall = useMemo(() => {
    const vals = rankedCandidates.map((c) => c._overall).filter((v) => v != null);
    return vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1) : "—";
  }, [rankedCandidates]);

  const jobIntegrityPct = useMemo(() => {
    const total = selectedJobInterviews.length;
    if (!total) return "—";
    const high = selectedJobInterviews.filter((i) => i.integrityScore != null && i.integrityScore >= 85).length;
    return `${Math.round((high / total) * 100)}%`;
  }, [selectedJobInterviews]);

  /* ── actions ── */
  const handleCreate = async (values) => {
    // Job creation only — CreateJobModal owns closing itself and toasting
    // success once it has also finished saving MCQ/Coding questions (if any)
    // for the new job, so this must not close the modal or toast early.
    const jobId = await createJob(values);
    loadInterviews();
    return jobId;
  };

  const copyLink = async (jobId) => {
    try { await navigator.clipboard.writeText(getInterviewLink(jobId)); toast.success("Link copied"); }
    catch { toast.error("Could not copy link"); }
  };

  const handleDownloadResults = () => {
    if (!selectedJob) return;
    if (isAssessmentJob) {
      const results = assessmentResults?.results || [];
      if (!results.length) { toast.error("No results to download yet"); return; }
      const isMcq = selectedJob.eventType === "MCQ";
      const hasCoding = results.some((r) => r.coding_total != null);
      const statusLabel = (s) => (
        s === "accepted" ? "Correct" : s === "not_run" || !s ? "Not attempted" : "Incorrect"
      );
      const formatBreakdown = (subs) =>
        (subs || []).map((s) => `${s.title || "Problem"}: ${statusLabel(s.status)}`).join("; ");
      const columns = [
        ["Name", (r) => r.name || "Candidate"],
        ["Email", (r) => r.email || ""],
        ...(isMcq
          ? [
              ["Score %", (r) => r.scores?.total ?? ""],
              ["Correct", (r) => r.correct_answers ?? 0],
              ["Total Questions", (r) => r.total_questions ?? 0],
              ["Time Used (min)", (r) => r.time_used_ms ? Math.round(r.time_used_ms / 60000) : ""]
            ]
          : [
              ["Solved", (r) => r.solved ?? 0],
              ["Total Problems", (r) => r.total ?? 0],
              ["Problem Breakdown", (r) => formatBreakdown(r.submissions)]
            ]),
        ...(hasCoding
          ? [
              ["Coding Solved/Total", (r) => r.coding_total != null ? `${r.coding_solved ?? 0}/${r.coding_total}` : ""],
              ["Coding Breakdown", (r) => formatBreakdown(r.coding_submissions)]
            ]
          : []),
        ["Integrity Score", (r) => r.integrity_score ?? ""],
        ["Integrity Level", (r) => r.integrity_level ?? ""],
        ["Screen Share Unsupported", (r) => (r.screen_share_unsupported ? "Yes" : "No")],
        ["Submitted At", (r) => r.created_at ?? ""]
      ];
      downloadCsv(`${selectedJob.title || "results"}_results.csv`, columns, results);
    } else {
      if (!rankedCandidates.length) { toast.error("No candidates to download yet"); return; }
      const columns = [
        ["Rank", (r) => r.rank],
        ["Candidate", (r) => r.candidateName || "Candidate"],
        ["Email", (r) => r.candidateEmail || ""],
        ["Technical", (r) => r.technicalScore ?? ""],
        ["Communication", (r) => r.communicationScore ?? ""],
        ["Confidence", (r) => r.confidenceScore ?? ""],
        ["Overall", (r) => r._overall ?? ""],
        ["Integrity Score", (r) => r.integrityScore ?? ""],
        ["Status", (r) => r.status ?? ""]
      ];
      downloadCsv(`${selectedJob.title || "results"}_candidates.csv`, columns, rankedCandidates);
    }
  };

  const openReport = async (jobId, interviewId) => {
    try {
      setReportLoading(true); setReportPending(false); setReportError(""); setSelectedReport(null);
      const report = await getRecruiterInterviewReport(jobId, interviewId);
      if (report?.pending) { setReportPending(true); toast("Report still generating"); }
      else { setSelectedReport(report); }
    } catch (err) { setReportError(err.message || "Unable to load report"); }
    finally { setReportLoading(false); }
  };

  const handleAllowInterviewRetake = async (jobId, interviewId) => {
    if (!window.confirm(
      "Allow this candidate to take the AI Interview again? Their previous transcript, " +
      "recording, and report stay exactly as they are — this only allows one more attempt."
    )) return;
    setRetakingInterviewId(interviewId);
    try {
      await allowInterviewRetake(jobId, interviewId);
      toast.success("Candidate can now retake this interview.");
      loadInterviews(true);
    } catch (err) {
      toast.error(err.message || "Unable to allow retake");
    } finally {
      setRetakingInterviewId(null);
    }
  };

  const isInitialLoading = loading || interviewsLoading;

  /* ═══════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════ */
  return (
    <div className="recruiter-jobs-page">
      <div className="recruiter-jobs-shell enterprise-dashboard">

        {/* ── Header ── */}
        <div className="recruiter-jobs-header">
          <div className="rd-header-left">
            {selectedJob && (
              <button className="rd-back-btn" type="button" onClick={() => setSelectedJobId("")}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                Hiring Overview
              </button>
            )}
            <h1>{selectedJob ? selectedJob.title : "Hiring Overview"}</h1>
            <p>
              {selectedJob
                ? <><StatusPill status={selectedJob.status} /><span style={{ marginLeft: 6 }}>
                    {isPipelineJob
                      ? `Hiring Pipeline · ${pipelineCandidateCount} candidates · ${pipelineSummary(selectedJob.pipelineStages)}`
                      : `${selectedJobInterviews.length} candidates · ${JOB_MODE_LABELS[selectedJob.mode] || selectedJob.mode}`}
                  </span></>
                : <>{filteredJobs.filter((j) => j.status === "ACTIVE").length} active openings · {filteredInterviews.length} candidates</>
              }
            </p>
          </div>
          <div className="header-actions">
            {selectedJob ? (
              <>
                {isPipelineJob ? (
                  <button className="secondary-button" type="button" onClick={() => setResumeUploadOpen(true)}>+ Add resume batch</button>
                ) : (
                  <button className="secondary-button" type="button" onClick={handleDownloadResults}>Download Results</button>
                )}
                {!isPipelineJob && (
                  <button className="secondary-button" type="button" onClick={() => copyLink(selectedJob.jobId)}>Copy Link</button>
                )}
                {!isPipelineJob && (
                  <button className="secondary-button" type="button" onClick={() => setBrandingOpen(true)}>Branding</button>
                )}
                <button
                  className={`status-toggle ${selectedJob.status === "ACTIVE" ? "active" : "inactive"}`}
                  type="button" onClick={() => toggleJobStatus(selectedJob)}
                >
                  {selectedJob.status === "ACTIVE" ? "Active" : "Inactive"}
                </button>
              </>
            ) : (
              <>
                <button className="secondary-button" type="button" onClick={() => { reload(); loadInterviews(); }}>Refresh</button>
                <button className="secondary-button" type="button" onClick={() => setPipelineWizardOpen(true)}>+ Create Hiring Pipeline</button>
                <button className="primary-button" type="button" onClick={() => setModalOpen(true)}>+ Create Job</button>
              </>
            )}
          </div>
        </div>

        {(error || interviewsError) && (
          <div className="recruiter-alert">
            <span>{error || interviewsError}</span>
            <button type="button" onClick={() => { reload(); loadInterviews(); }}>Retry</button>
          </div>
        )}

        <div className="dashboard-content-pad">
          {isInitialLoading ? <DashboardSkeleton /> : (
            <>
              {/* ── Filters ── */}
              <DashboardFilters
                search={search} status={statusFilter} mode={modeFilter}
                onSearchChange={setSearch} onStatusChange={setStatusFilter} onModeChange={setModeFilter}
              />

              {/* ── KPI row ── */}
              <div className="summary-card-grid">
                <KpiCard
                  label="Active Jobs"
                  value={filteredJobs.filter((j) => j.status === "ACTIVE").length}
                  detail={`${filteredJobs.length} total roles`}
                />
                <KpiCard
                  label="Candidates Evaluated"
                  value={filteredInterviews.length}
                  detail={`${completedInterviews.length} completed`}
                />
                <KpiCard
                  label="Avg. Overall Score"
                  value={avgOverall}
                  detail="composite across roles"
                />
                <KpiCard
                  label="Integrity Flags"
                  value={flaggedCount}
                  detail="score below 70"
                  accent
                />
              </div>

              {/* ══════════════════════════════
                  JOB RESULTS VIEW
                  ══════════════════════════════ */}
              {selectedJob ? (
                isPipelineJob ? (
                  <PipelineCandidateTable
                    key={candidateTableRefreshKey}
                    jobId={selectedJob.jobId}
                    job={selectedJob}
                    onCandidateCountChange={setPipelineCandidateCount}
                  />
                ) : isAssessmentJob ? (
                  <AssessmentResultsSection
                    jobId={selectedJob.jobId}
                    eventType={selectedJob.eventType}
                    loading={assessmentLoading}
                    error={assessmentError}
                    data={assessmentResults}
                    onRetakeGranted={() => loadAssessmentResults(true)}
                  />
                ) : (
                <>
                  {/* Mini KPIs */}
                  <div className="summary-card-grid">
                    <KpiCard label="Candidates" value={selectedJobInterviews.length} detail={`${selectedJobInterviews.filter(i=>i.status==="COMPLETED").length} completed`} />
                    <KpiCard label="Avg. Technical"     value={jobAvgTech}        detail="out of 100" />
                    <KpiCard label="Avg. Overall"       value={jobAvgOverall}     detail="composite score" />
                    <KpiCard label="High Integrity"     value={jobIntegrityPct}   detail="score ≥ 85" accent />
                  </div>

                  {selectedJobInterviews.length === 0 ? (
                    <div className="recruiter-empty">No candidates have attempted this role yet.</div>
                  ) : (
                    <>
                      {/* Split: Scatter + Skill breakdown */}
                      <div className="rd-split">
                        {/* Score distribution */}
                        <div className="recruiter-section">
                          <div className="section-heading">
                            <h2>Score distribution</h2>
                            <span className="section-pill">Technical vs. Overall</span>
                          </div>
                          <div className="rd-scatter-wrap">
                            <ResponsiveContainer width="100%" height={260}>
                              <ScatterChart margin={{ top: 12, right: 16, bottom: 24, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                                <XAxis dataKey="technical" type="number" domain={[0, 100]}
                                  tick={{ fill: "rgba(255,255,255,0.42)", fontSize: 10 }}
                                  label={{ value: "Technical →", position: "insideBottomRight", offset: -4, fill: "rgba(255,255,255,0.38)", fontSize: 10 }} />
                                <YAxis dataKey="overall" type="number" domain={[0, 100]}
                                  tick={{ fill: "rgba(255,255,255,0.42)", fontSize: 10 }}
                                  label={{ value: "Overall →", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.38)", fontSize: 10 }} />
                                <Tooltip content={<ScatterTip />} cursor={{ stroke: "rgba(255,255,255,0.12)" }} />
                                <Scatter data={scatterData}
                                  shape={(props) => {
                                    const { cx, cy, payload } = props;
                                    const top = payload.technical >= 75 && payload.overall >= 75;
                                    const r = payload.rank <= 3 ? 7 : 5;
                                    return (
                                      <g>
                                        <circle cx={cx} cy={cy} r={r + 3} fill={top ? "#f59e0b" : "#764ba2"} fillOpacity={0.15} />
                                        <circle cx={cx} cy={cy} r={r} fill={top ? "#f59e0b" : "#667eea"} stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
                                      </g>
                                    );
                                  }}
                                />
                              </ScatterChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Skill breakdown */}
                        <div className="recruiter-section">
                          <div className="section-heading">
                            <h2>Skill breakdown</h2>
                            <span className="section-pill">Cohort averages</span>
                          </div>
                          <div className="rd-pipeline">
                            {skillAverages.map(({ label, avg }) => (
                              <PipelineBar key={label} label={label} value={avg} max={100}
                                color={avg >= 75 ? "#36d399" : avg >= 55 ? "#667eea" : "#f59e0b"} />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Ranked candidates table */}
                      <div className="recruiter-section" style={{ marginTop: "1rem" }}>
                        <div className="section-heading">
                          <h2>Ranked candidates</h2>
                          <span className="section-pill">{rankedCandidates.length} total</span>
                        </div>
                        <div className="enterprise-table-wrap">
                          <table className="enterprise-table candidate-detail-table">
                            <thead>
                              <tr>
                                <th style={{ width: 52 }}>Rank</th>
                                <th>Candidate</th>
                                <th>Technical</th>
                                <th>Communication</th>
                                <th>Confidence</th>
                                <th>Overall</th>
                                <th>Integrity</th>
                                <th>Date &amp; Time</th>
                                <th style={{ width: 90 }}>Report</th>
                                <th style={{ width: 105 }}>Recording</th>
                                <th style={{ width: 100 }}>Retake</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedCandidates.rows.map((interview) => {
                                const overall = interview._overall;
                                return (
                                  <tr key={interview.interviewId} className={interview.rank === 1 ? "rd-row-top" : ""}>
                                    <td><RankBadge rank={interview.rank} /></td>
                                    <td>
                                      <div className="rd-cand-name">
                                        <div className="rd-cand-avatar">{(interview.candidateName || "?").slice(0, 2).toUpperCase()}</div>
                                        <div>
                                          <div className="rd-cand-display">{interview.candidateName || "Candidate"}</div>
                                          <div className="rd-cand-email">{interview.candidateEmail || ""}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td><ScoreBar value={interview.technicalScore} /></td>
                                    <td><ScoreBar value={interview.communicationScore} /></td>
                                    <td><ScoreBar value={interview.confidenceScore} /></td>
                                    <td>
                                      <span className={`rd-overall ${scoreColor(overall)}`}>
                                        {overall ?? "—"}
                                      </span>
                                    </td>
                                    <td>
                                      <IntegrityRing
                                        score={interview.integrityScore}
                                        level={
                                          interview.integrityScore == null ? "pending"
                                          : interview.integrityScore >= 85 ? "high"
                                          : interview.integrityScore >= 70 ? "medium"
                                          : "low"
                                        }
                                        flags={[]}
                                      />
                                    </td>
                                    <td className="rd-muted">{formatDateTime(interview.completedAt || interview.startedAt)}</td>
                                    <td>
                                      <button
                                        className="rd-action-btn"
                                        type="button"
                                        onClick={() => openReport(interview.jobId, interview.interviewId)}
                                      >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h6"/></svg>
                                        {interview.reportStatus === "COMPLETED" ? "View" : "Report"}
                                      </button>
                                    </td>
                                    <td>
                                      {interview.recordingUrl ? (
                                        <button
                                          className="rd-action-btn rd-action-rec"
                                          type="button"
                                          onClick={() => setRecordingPreview(interview)}
                                        >
                                          <span className="rd-play-dot">
                                            <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                                          </span>
                                          Play
                                        </button>
                                      ) : (
                                        <span className="rd-pending">Pending</span>
                                      )}
                                    </td>
                                    <td>
                                      {retakePendingByUid[interview.candidateUid] ? (
                                        <span
                                          className="rd-retake-pending"
                                          title="This candidate's attempt lock was cleared and they haven't started a new attempt yet"
                                        >
                                          ✓ Retake allowed
                                        </span>
                                      ) : (
                                        <button
                                          className="rd-action-btn"
                                          type="button"
                                          title="Let this candidate take the AI Interview again — their previous transcript/recording/report stay untouched"
                                          disabled={retakingInterviewId === interview.interviewId}
                                          onClick={() => handleAllowInterviewRetake(interview.jobId, interview.interviewId)}
                                        >
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 1 3 6.7M3 21v-6h6"/></svg>
                                          {retakingInterviewId === interview.interviewId ? "…" : "Allow Retake"}
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <PaginationControls
                            label="candidates"
                            total={rankedCandidates.length}
                            page={paginatedCandidates.currentPage}
                            totalPages={paginatedCandidates.totalPages}
                            start={paginatedCandidates.start}
                            end={paginatedCandidates.end}
                            onPageChange={setCandidatePage}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
                )
              ) : (
                /* ══════════════════════════════
                   JOBS LIST VIEW
                   ══════════════════════════════ */
                jobs.length === 0 ? (
                  <div className="recruiter-empty">
                    <h2>No jobs yet</h2>
                    <p>Create your first job opening to start receiving candidates.</p>
                  </div>
                ) : (
                  <div className="rd-split">

                    {/* ── Left: Open positions table ── */}
                    <div className="recruiter-section">
                      <div className="section-heading">
                        <div>
                          <h2>Open positions</h2>
                          <span style={{ display: "block", fontSize: "11.5px", color: "rgba(255,255,255,0.42)", marginTop: "2px" }}>
                            Click a row to view candidate rankings
                          </span>
                        </div>
                        <div className="rd-tabs">
                          {[["ALL", `All · ${filteredJobs.length}`], ["ACTIVE", `Active · ${filteredJobs.filter(j=>j.status==="ACTIVE").length}`], ["INACTIVE", `Inactive · ${filteredJobs.filter(j=>j.status==="INACTIVE").length}`]].map(([key, lbl]) => (
                            <button key={key} className={jobTabFilter === key ? "on" : ""} onClick={() => setJobTabFilter(key)}>{lbl}</button>
                          ))}
                        </div>
                      </div>

                      <div className="enterprise-table-wrap">
                        <table className="enterprise-table role-table">
                          <thead>
                            <tr>
                              <th>Position</th>
                              <th>Status</th>
                              <th>Candidates</th>
                              <th>Avg. score</th>
                              <th>Created</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedJobs.rows.map((job) => {
                              const jobInterviews = interviews.filter((i) => i.jobId === job.jobId);
                              const attempted = jobInterviews.length || job.candidateCount || 0;
                              const pct = Math.min(100, attempted * 2);
                              const avg = jobAvgScore(job.jobId);
                              const isPipeline = (job.pipelineStages || []).length > 0;
                              const isInterview = !job.eventType || job.eventType === "INTERVIEW";
                              return (
                                <tr
                                  key={job.jobId}
                                  className="rd-job-row clickable"
                                  onClick={() => setSelectedJobId(job.jobId)}
                                >
                                  <td>
                                    <div className="rd-job-title">
                                      <div className="rd-job-icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="14" rx="2"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M3 12h18"/></svg>
                                      </div>
                                      <div>
                                        <div className="rd-job-name">
                                          {job.title || "Untitled"}
                                          <EventTypeBadge eventType={job.eventType} includeCoding={!!job.config?.include_coding} pipeline={isPipeline} />
                                        </div>
                                        <div className="rd-job-dept">
                                          {job.companyName} · {isPipeline
                                            ? pipelineSummary(job.pipelineStages)
                                            : isInterview ? (JOB_MODE_LABELS[job.mode] || job.mode) : "company-supplied questions"}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td><StatusPill status={job.status} /></td>
                                  <td>
                                    <div className="rd-cand-cell">
                                      <div className="rd-cand-bar"><span style={{ width: `${pct}%` }} /></div>
                                      <span className="rd-cand-num">{attempted}</span>
                                    </div>
                                  </td>
                                  <td>
                                    {avg
                                      ? <span className="rd-score-chip">{avg}</span>
                                      : <span className="rd-muted">—</span>}
                                  </td>
                                  <td><span className="rd-muted">{formatDate(job.createdAt)}</span></td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <div className="quick-actions">
                                      {isPipeline ? (
                                        <button className="link-button" type="button" onClick={() => setSelectedJobId(job.jobId)}>Open Pipeline</button>
                                      ) : (
                                        <>
                                          <button className="link-button" type="button" onClick={() => copyLink(job.jobId)}>Copy Link</button>
                                          <button className="link-button" type="button" onClick={() => setManageQuestionsJob(job)}>Manage Questions</button>
                                        </>
                                      )}
                                      <button
                                        className={`status-toggle ${job.status === "ACTIVE" ? "active" : "inactive"}`}
                                        type="button" onClick={() => toggleJobStatus(job)}
                                      >
                                        {job.status === "ACTIVE" ? "Active" : "Inactive"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <PaginationControls
                          label="roles"
                          total={tabFilteredJobs.length}
                          page={paginatedJobs.currentPage}
                          totalPages={paginatedJobs.totalPages}
                          start={paginatedJobs.start}
                          end={paginatedJobs.end}
                          onPageChange={setRolePage}
                        />
                      </div>
                    </div>

                    {/* ── Right column ── */}
                    <div className="rd-right-col">

                      {/* Candidate pipeline */}
                      <div className="recruiter-section">
                        <div className="section-heading">
                          <h2>Candidate pipeline</h2>
                          <span className="section-pill">All roles</span>
                        </div>
                        <div className="rd-pipeline">
                          <PipelineBar label="Invited"   value={pipelineInvited}   max={pipelineMax} color="#667eea" />
                          <PipelineBar label="Started"   value={pipelineStarted}   max={pipelineMax} color="#764ba2" />
                          <PipelineBar label="Completed" value={pipelineCompleted} max={pipelineMax} color="#5b3a87" />
                          <PipelineBar label="High integrity" value={filteredInterviews.filter(i=>i.integrityScore>=85).length} max={pipelineMax} color="#36d399" />
                          <PipelineBar label="Flagged"   value={flaggedCount} max={pipelineMax} color="#f59e0b" />
                        </div>
                      </div>

                      {/* Recent activity */}
                      <div className="recruiter-section">
                        <div className="section-heading">
                          <h2>Recent activity</h2>
                        </div>
                        {recentActivity.length === 0 ? (
                          <div className="recruiter-empty compact">No activity yet.</div>
                        ) : (
                          <div className="rd-activity-list">
                            {recentActivity.map((item) => {
                              const score = computeOverall(item);
                              const isCompleted = item.status === "COMPLETED";
                              const isFlagged = item.integrityScore != null && item.integrityScore < 70;
                              return (
                                <div key={item.interviewId} className="rd-activity-item">
                                  <div className={`rd-activity-ico ${isCompleted ? (isFlagged ? "warn" : "good") : "info"}`}>
                                    {isCompleted && !isFlagged
                                      ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                                      : isFlagged
                                        ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 9v4M10.3 3.86L1.82 18A2 2 0 003.24 21h17.52A2 2 0 0022.5 18L13.71 3.86a2 2 0 00-3.42 0z"/><circle cx="12" cy="17" r="0.8" fill="currentColor"/></svg>
                                        : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M3 21a9 9 0 0118 0"/></svg>}
                                  </div>
                                  <div className="rd-activity-body">
                                    <span>
                                      <strong>{item.candidateName || "Candidate"}</strong>
                                      {isCompleted
                                        ? <> completed interview{score != null && <> · <strong>{score}/100</strong></>}</>
                                        : isFlagged
                                          ? <> integrity flag raised</>
                                          : <> started interview</>}
                                    </span>
                                    <div className="rd-activity-time">{formatDate(item.completedAt || item.startedAt)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                    </div>{/* end rd-right-col */}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modals (unchanged) ── */}
      <CreateJobModal open={modalOpen} saving={saving} onClose={() => setModalOpen(false)} onCreate={handleCreate} />
      <CreateJobWizard open={pipelineWizardOpen} saving={saving} onClose={() => setPipelineWizardOpen(false)} onCreate={handleCreate} />
      {selectedJob && (
        <ResumeUploadModal
          open={resumeUploadOpen}
          jobId={selectedJob.jobId}
          onClose={() => setResumeUploadOpen(false)}
          onBatchStarted={() => setCandidateTableRefreshKey((k) => k + 1)}
        />
      )}
      {selectedJob && (
        <JobBrandingModal
          open={brandingOpen}
          jobId={selectedJob.jobId}
          jobTitle={selectedJob.title}
          onClose={() => setBrandingOpen(false)}
        />
      )}

      {manageQuestionsJob && (
        <div className="recruiter-modal-backdrop" role="presentation">
          <div className="recruiter-modal recruiter-modal--wide" role="dialog" aria-modal="true" aria-labelledby="manage-questions-title">
            <div className="recruiter-modal-header">
              <div>
                <h2 id="manage-questions-title">{manageQuestionsJob.title} — Questions</h2>
                <p>{EVENT_TYPE_LABELS[manageQuestionsJob.eventType] || manageQuestionsJob.eventType}</p>
              </div>
              <button className="icon-button" type="button" onClick={() => setManageQuestionsJob(null)} aria-label="Close">x</button>
            </div>
            <QuestionBankManager
              jobId={manageQuestionsJob.jobId}
              eventType={manageQuestionsJob.eventType}
              includeCoding={!!manageQuestionsJob.config?.include_coding}
              companyName={manageQuestionsJob.companyName}
              onClose={() => setManageQuestionsJob(null)}
            />
          </div>
        </div>
      )}

      <RecruiterReportDetail
        report={selectedReport} pending={reportPending} loading={reportLoading} error={reportError}
        onClose={() => { setSelectedReport(null); setReportPending(false); setReportError(""); }}
      />

      {recordingPreview && (
        <div className="recording-preview-backdrop" role="dialog" aria-modal="true">
          <div className="recording-preview-panel">
            <div className="recording-preview-header">
              <div>
                <h2>Interview Recording</h2>
                <p>{recordingPreview.candidateName || "Candidate"} | {recordingPreview.jobTitle || selectedJob?.title || "Role"}</p>
              </div>
              <button type="button" className="link-button" onClick={() => setRecordingPreview(null)}>Close</button>
            </div>
            <video controls src={recordingPreview.recordingUrl} preload="metadata">
              Your browser does not support video playback.
            </video>
          </div>
        </div>
      )}
    </div>
  );
}
